import { useLayoutEffect, useRef, type RefObject } from "react";

const layers: HTMLDialogElement[] = [];
let pageOverflow = "", pageGutter = "";
let layerRevision = 0;
export const isTopModal = (node: HTMLDialogElement | null) => node != null && layers.at(-1) === node;

/* 창이 창 위에 서면 가림막도 겹쳐 쌓여, 한 걸음 들어갔을 뿐인데 뒤가 두 배로 어두워진다.
   어둡기는 "지금 판을 못 쓴다"는 한 가지 사실을 말하는 것이라 깊이에 따라 달라질 이유가
   없다 — 맨 아래 창만 가림막을 칠하고, 그 위에 선 창들은 투명하게 둔다. */
const syncBackdrops = () => layers.forEach((layer, index) => {
  /* 가림막은 한 겹만 깔린다. 칠하는 것은 "뒤를 잠그는 창" 가운데 가장 위에 있는 것이다 —
     맨 위 창이 무조건 칠하게 두었더니, 그 자리에 뒤를 안 잠그는 작은 창(개인정보)이 서는
     순간 가림막이 통째로 사라져 밑의 큰 창까지 훤해졌다. 작은 창은 제 그림자로 떠 있고,
     어둡게 하는 일은 그 아래 큰 창이 계속 맡는다. */
  const dimmers = layers.filter(value => !value.classList.contains("ui-dialog--over"));
  if (layer === dimmers.at(-1)) delete layer.dataset.stacked;
  else layer.dataset.stacked = "true";
  /* 한 걸음 안으로 들어가면 창이 하나 더 쌓이는 게 아니라, 보고 있던 창이 그 내용으로
     바뀌어야 한다 — 두 장이 겹쳐 보이면 뒤의 것이 아직 살아 있는 화면처럼 읽히고, 모서리가
     어긋나 두 층이 그대로 드러난다. 덮인 창은 자리를 지킨 채 모습만 감춘다: 닫히면 적어 둔
     것도 스크롤도 그대로 돌아온다. */
  /* 덮인 창을 감추는 것은 위에 선 창이 같은 자리를 통째로 쓸 때만이다 — 그때는 한 창이
     제 내용을 바꾼 것으로 읽힌다. 작은 창(개인정보·되묻기)은 그렇지 않다: 연 자리가 어디인지
     보이려면 뒤의 큰 창이 그대로 남아 있어야 하고, 사라지면 어디서 왔는지가 끊긴다. */
  const above = layers[index + 1];
  const replaces = above && !above.classList.contains("ui-dialog--over") && !above.classList.contains("ui-dialog--confirm");
  if (!replaces) delete layer.dataset.covered;
  else layer.dataset.covered = "true";
});

const available = (element: HTMLElement) => element.isConnected && !element.matches(":disabled,[aria-disabled=true]") &&
  !element.closest("[inert],[hidden]") && !!element.getClientRects().length && !["hidden", "collapse"].includes(getComputedStyle(element).visibility);

export const dialogFocusTargets = (node: HTMLDialogElement) => [...node.querySelectorAll<HTMLElement>("button,input,textarea,select,a[href],[tabindex]")]
  .filter(element => element.tabIndex >= 0 && available(element));

function focus(element: HTMLElement | null | undefined) {
  if (!element || !available(element)) return false;
  const temporary = element.tabIndex < 0 && !element.hasAttribute("tabindex");
  if (temporary) element.tabIndex = -1;
  element.focus({ preventScroll: true });
  if (temporary) {
    if (document.activeElement === element) element.addEventListener("blur", () => element.removeAttribute("tabindex"), { once: true });
    else element.removeAttribute("tabindex");
  }
  return document.activeElement === element;
}

/** Top-layer ownership, page locking and focus destinations are one lifecycle. */
export function useModalLayer(ref: RefObject<HTMLDialogElement | null>, restoreFocus?: () => HTMLElement | null) {
  const restore = useRef(restoreFocus); restore.current = restoreFocus;
  useLayoutEffect(() => {
    const node = ref.current!;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = document.documentElement;
    if (!layers.length) {
      pageOverflow = root.style.overflow; pageGutter = root.style.scrollbarGutter;
      if (root.scrollHeight > root.clientHeight) root.style.scrollbarGutter = "stable";
      root.style.overflow = "hidden";
    }
    layers.push(node); layerRevision++; syncBackdrops(); node.showModal();
    if (!node.querySelector("[autofocus]")) node.focus({ preventScroll: true });
    return () => {
      const top = isTopModal(node), index = layers.indexOf(node);
      if (index >= 0) { layers.splice(index, 1); syncBackdrops(); }
      const revision = ++layerRevision;
      node.close();
      if (!layers.length) { root.style.overflow = pageOverflow; root.style.scrollbarGutter = pageGutter; }
      const remaining = layers.at(-1);
      // Layout cleanup runs before sibling DOM updates. A saved form's opener
      // may still be disabled until this commit finishes; inspect it afterwards.
      // Never let an older close steal focus from a newly mounted dialog.
      queueMicrotask(() => {
        if (revision !== layerRevision) return;
        if (!top) {
          if (remaining && !remaining.contains(document.activeElement)) focus(remaining);
          return;
        }
        const requested = restore.current?.();
        for (const candidate of [requested, previous]) {
          if (candidate && (!remaining || remaining.contains(candidate)) && focus(candidate)) return;
        }
        if (remaining) { focus(remaining); return; }
        focus(document.getElementById("main-content") ?? document.querySelector("main") ?? document.body);
      });
    };
  }, []);
}
